import { pickT } from "@repo/shared";
import type { PortfolioType } from "@repo/shared/types";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { T } from "./default-portfolio-type-select.i18n";

// QL-697 — the portfolio_input "Default portfolio type": the type a NEW
// portfolio starts with, over the allowed types. Shared by the add and the
// settings dialog. "Automatic" (no default) keeps the old rule — model when
// allowed, else the first allowed type — which the backend applies too
// (`resolveDefaultPortfolioType`). Existing portfolios keep their type.

const AUTOMATIC = "__automatic__";

const ORDER: PortfolioType[] = ["model", "custom", "dynamic"];

type DefaultPortfolioTypeSelectProps = {
  flowLanguage: string;
  allowedPortfolioTypes: PortfolioType[];
  defaultPortfolioType: PortfolioType | undefined;
  onDefaultPortfolioTypeChange: (type: PortfolioType | undefined) => void;
};

export function DefaultPortfolioTypeSelect({
  flowLanguage,
  allowedPortfolioTypes,
  defaultPortfolioType,
  onDefaultPortfolioTypeChange,
}: DefaultPortfolioTypeSelectProps) {
  const t = pickT(T, flowLanguage);
  // With one allowed type there is nothing to choose.
  if (allowedPortfolioTypes.length < 2) return null;

  return (
    <div className="space-y-2">
      <Label htmlFor="portfolio-input-default-type" className="text-base">
        {t.defaultPortfolioType}
      </Label>
      <p className="text-sm text-muted-foreground">{t.defaultPortfolioTypeDesc}</p>
      <Select
        value={defaultPortfolioType ?? AUTOMATIC}
        onValueChange={(value) =>
          onDefaultPortfolioTypeChange(value === AUTOMATIC ? undefined : (value as PortfolioType))
        }
      >
        <SelectTrigger id="portfolio-input-default-type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTOMATIC}>{t.automatic}</SelectItem>
          {ORDER.filter((type) => allowedPortfolioTypes.includes(type)).map((type) => (
            <SelectItem key={type} value={type}>
              {t[type]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
